import "./site/error-beacon";
import { Game, GameState, RunResult } from "./game/game";
import { fetchConfig, finishRun, flushQueue } from "./game/api";
import { recordRun as recordProfileRun, showXpToast } from "./site/profile";
import { getDailySeed as getPlatformDailySeed, markDailyComplete, isDailyRun as isPlatformDailyRun } from "./site/daily";
import { LeaderboardView } from "./ui/leaderboard";
import { VirtualJoystick } from "./ui/joystick";
import { Gyro } from "./ui/gyro";
import { SettingsDrawer } from "./ui/settings-drawer";
import { profileFor, QualityPreset, Settings, loadSettings, saveSettings } from "./ui/settings";
import { TurnstileGuard } from "./ui/turnstile";
import { FocusTrap } from "./ui/focus-trap";
import { getBest, recordScore } from "./ui/personal-best";
import { Achievements, makeToaster } from "./ui/achievements";
import { dailySeed, recordDailyBest, getDailyBest, todayKey, bumpDailyStreak } from "./ui/daily-seed";
import { getVariant, unlockedVariants } from "./ui/ship-variants";
import { BIOMES } from "./game/biomes";
import { maybeShowOnboarding } from "./ui/onboarding";
import { ReplayRecorder } from "./ui/replay";
import { maybeMountPerfHud } from "./ui/perf-hud";
import { recordRun, load as loadHistory, renderSparkline } from "./ui/run-history";
import { recordMiss, renderHeatmap } from "./ui/miss-heatmap";
import { biomeFor } from "./game/biomes";
import "./ui/styles.css";

function $(sel: string) {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`missing element: ${sel}`);
  return el as HTMLElement;
}

const canvas = $("#stage") as HTMLCanvasElement;
const hud = $(".hud") as HTMLElement;
const hudScore = $("#hud-score");
const hudTime = $("#hud-time");
const hudMult = $("#hud-mult");
const hudMultFill = $("#hud-mult-fill");
const msgTitle = $("#msg-title");
const msgBody = $("#msg-body");
const actBanner = document.getElementById("act-banner");
let actBannerTimer = 0;
function showActBanner(name: string, ringIdx: number) {
  if (!actBanner) return;
  actBanner.innerHTML = `<div class="ms-title">ENTERING THE ${name}</div><div class="ms-sub">Ring ${ringIdx} · +1500 boss bonus</div>`;
  actBanner.classList.add("visible");
  window.clearTimeout(actBannerTimer);
  actBannerTimer = window.setTimeout(() => actBanner.classList.remove("visible"), 2600);
}
const btnStart = document.querySelector('[data-action="start"]') as HTMLButtonElement | null;
const btnPlay = document.querySelector('[data-action="play"]') as HTMLButtonElement | null;
const btnPause = $("#btn-pause") as HTMLButtonElement;
const btnShare = $("#btn-share") as HTMLButtonElement;
const handleInput = $("#handle-input") as HTMLInputElement;
const handleInputOverlay = $("#handle-input-overlay") as HTMLInputElement;
const handleError = $("#handle-error") as HTMLElement;
const submitForm = $("#submit-form") as HTMLFormElement;
const btnSubmitScore = $("#btn-submit-score") as HTMLButtonElement;
const lbList = $("#leaderboard-list") as HTMLOListElement;
const runStats = $("#run-stats") as HTMLElement;
const webglError = $("#webgl-error");

function hasWebGL(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}

if (!hasWebGL()) {
  webglError.hidden = false;
  throw new Error("WebGL unavailable");
}

// Persisted handle pre-fills both the in-game tag AND the post-run overlay form
const savedHandle = localStorage.getItem("aether.handle") ?? "";
if (savedHandle) {
  handleInput.value = savedHandle;
  handleInputOverlay.value = savedHandle;
}

// Background drift while idle: game runs visually, no state change yet.
const leaderboard = new LeaderboardView(lbList);
leaderboard.refresh();
flushQueue().then(() => leaderboard.refresh());

function setOverlayMessage(state: GameState, finalScore?: number) {
  hud.dataset.state = state;
  if (state === "idle") {
    msgTitle.textContent = "Aether Drift";
    msgBody.textContent = "Pass rings to extend your drift. Miss them and time burns.";
    if (btnStart) btnStart.textContent = "Start run";
    runStats.hidden = true;
  } else if (state === "paused") {
    msgTitle.textContent = "Paused";
    const score = parseInt(hudScore.textContent?.replace(/[^\d]/g, "") || "0", 10);
    const time = parseFloat(hudTime.textContent || "0");
    msgBody.textContent = `Score ${score.toLocaleString()} · ${time.toFixed(1)}s left. Take a breath.`;
    if (btnStart) btnStart.textContent = "Resume";
    runStats.hidden = true;
  } else if (state === "over") {
    msgTitle.textContent = "Drift ended";
    msgBody.textContent = `Final score: ${finalScore?.toLocaleString() ?? 0}`;
    if (btnStart) btnStart.textContent = "Run again";
  }
}

// Holds the most-recent run result while we wait for the player to enter
// their handle in the post-drift form. Reset after submit.
let pendingRun: RunResult | null = null;

// Keyboard trap for the submit form so Tab can't escape onto the canvas.
const overlayTrap = new FocusTrap();

/** Format the rank blurb so it reads sensibly when total is small. */
function rankBlurb(res: { rank: number; percentile: number; total?: number }): string {
  const total = res.total;
  if (total === 1) return "first run on the board";
  if (res.rank === 1) return total ? `#1 of ${total}` : "#1";
  if (total) return `rank #${res.rank} of ${total} · top ${(100 - res.percentile).toFixed(1)}%`;
  return `rank #${res.rank} · top ${(100 - res.percentile).toFixed(1)}%`;
}

// Auto-downgrade quality on sustained slow frames. Steps high → medium → low
// at most once per pressure event; the engine enforces a 5s cooldown between
// signals so we don't ping-pong.
const DOWNGRADE: Record<QualityPreset, QualityPreset | null> = {
  high: "medium",
  medium: "low",
  low: null,
};
function autoDowngradeQuality(avgMs: number) {
  const s = loadSettings();
  const next = DOWNGRADE[s.quality];
  if (!next) return;
  const updated = { ...s, quality: next };
  saveSettings(updated);
  game.applyQualityProfile(profileFor(next));
  console.info(`[perf] frame avg ${avgMs.toFixed(1)}ms — auto-downgrade ${s.quality} → ${next}`);
}

// Lifetime biome set (persisted) drives the Cartographer achievement.
const BIOMES_KEY = "aether.biomes_seen";
const biomesSeen = new Set<number>(
  ((): number[] => {
    try { return JSON.parse(localStorage.getItem(BIOMES_KEY) || "[]"); } catch { return []; }
  })(),
);
const ach = new Achievements(makeToaster());

// First-run tutorial overlay. Returns null when already dismissed in a
// prior session. Auto-dismiss on first ring pass or after 10s.
let onboard = maybeShowOnboarding();

// Replay recorder — single instance, started on each run, stopped on
// game.end(). Surfaces a download button when a blob is available.
const replay = new ReplayRecorder();

// Sharable seed URL: `?seed=NNNN` runs that seed (local-only, like daily).
// Parsed once at boot; settings.daily can still override it via priority.
function parseSeedParam(): number | null {
  const raw = new URLSearchParams(location.search).get("seed");
  if (!raw) return null;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0 || n > 0x7fffffff) return null;
  return n;
}
const urlSeed = parseSeedParam();

/** Rotate the hue of an RGB hex by `deg` degrees, preserving L+S. */
function rotateHueHex(hex: number, deg: number): number {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  h = (h + deg / 360) % 1;
  if (h < 0) h += 1;
  // back to rgb
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const rr = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const gg = Math.round(hue2rgb(p, q, h) * 255);
  const bb = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return (rr << 16) | (gg << 8) | bb;
}

// Dev HUD — only mounts when ?debug=1 is in the URL. Pulls perf via a
// stable snapshot fn on the Game instance.
maybeMountPerfHud(() => game.perfSnapshot());

const game = new Game(canvas, {
  onStateChange: (s) => {
    if (s !== "over") setOverlayMessage(s);
    // Endless-mode "End run" button: visible while running or paused AND
    // the active run is endless. (Use game.isEndless rather than the
    // settings flag so an in-flight timed run doesn't grow the button
    // when the player flips the pill mid-run — they'd have to reset.)
    const endRunBtn = document.getElementById("btn-end-run");
    if (endRunBtn) {
      const show = game.isEndless && (s === "running" || s === "paused");
      endRunBtn.hidden = !show;
    }
    // Focus-trap on the pause overlay so keyboard users can't tab onto the
    // canvas and lose focus. Esc resumes. Released on running/over.
    const screenMsg = document.getElementById("screen-msg");
    if (s === "paused" && screenMsg) {
      overlayTrap.activate(screenMsg, () => game.resume());
    } else if (s === "running") {
      overlayTrap.deactivate();
    }
    if (s === "running") {
      replay.start(canvas);
      // When modifiers are enabled, surface the active one as a brief
      // toast so the player knows what's been twisted for this run.
      const mod = game.activeModifier;
      if (mod) {
        const label = mod.replace(/_/g, " ");
        const el = document.createElement("div");
        el.className = "modifier-toast";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        el.textContent = `Modifier: ${label}`;
        document.body.appendChild(el);
        requestAnimationFrame(() => el.classList.add("in"));
        setTimeout(() => {
          el.classList.remove("in");
          el.classList.add("out");
          setTimeout(() => el.remove(), 350);
        }, 2500);
      }
    }
  },
  onPerfPressure: autoDowngradeQuality,
  onRingEvent: (e) => {
    if (e.type === "pass" && onboard) {
      onboard.dismiss();
      onboard = null;
    }
    if (e.type === "miss") {
      recordMiss(e.missLateralSigned);
    }
    if (e.type === "pass") {
      const idx = biomeFor(e.ringIdx).index;
      if (!biomesSeen.has(idx)) {
        biomesSeen.add(idx);
        try { localStorage.setItem(BIOMES_KEY, JSON.stringify([...biomesSeen])); } catch {}
      }
    }
    if (e.boss) {
      // Boss ring (every 50th) — heavy magenta flash, double the gate flash
      // duration. Class is mutually exclusive with gate-flash so they don't
      // composite the same pseudo-element twice.
      document.body.classList.add("boss-flash");
      setTimeout(() => document.body.classList.remove("boss-flash"), 480);
      // v4 — act-transition banner. Only show on pass (a missed boss
      // ring is its own punishment).
      if (e.type === "pass") {
        const ACT_NAMES_CLIENT = ["Open", "River", "Chase"];
        const actIdx = Math.min(ACT_NAMES_CLIENT.length - 1, Math.floor(e.ringIdx / 50));
        showActBanner(ACT_NAMES_CLIENT[actIdx].toUpperCase(), e.ringIdx);
      }
    } else if (e.gate) {
      // Gate ring (every 13th passed) — brief screen flash via CSS class.
      // Toggle removes itself after the animation so the class is reusable.
      document.body.classList.add("gate-flash");
      setTimeout(() => document.body.classList.remove("gate-flash"), 260);
    }
    ach.check({
      score: e.score,
      streak: e.streak,
      passCount: e.type === "pass" ? 1 : 0,
      noMissSeconds: e.noMissSeconds,
      biomesVisited: biomesSeen,
    });
    // Lifetime peak no-miss-seconds — drives the Clean Minute progress
    // bar in the achievement panel. Update whenever this run sets a new
    // local peak; the value is reset to 0 only when the player clears
    // localStorage.
    try {
      const prev = parseInt(localStorage.getItem("aether.no_miss_best") || "0", 10) || 0;
      const seconds = Math.floor(e.noMissSeconds);
      if (seconds > prev) localStorage.setItem("aether.no_miss_best", String(seconds));
    } catch { /* quota / private mode */ }
  },
  onHud: (score, time, mult) => {
    hudScore.textContent = score.toLocaleString();
    hudTime.textContent = time.toFixed(1);
    hudMult.textContent = `${mult.toFixed(1)}`;
    // Meter ramps from 0 (mult=1) to 100% (mult=3, MAX_MULT) so the bar
    // visually echoes the numeric. Mult-buff orb pushes effective mult
    // beyond 3 client-side; meter saturates at full.
    const pct = Math.min(100, Math.max(0, ((mult - 1) / 2) * 100));
    hudMultFill.style.width = `${pct}%`;
    // Tier-driven color shift on the ×MULT numeric:
    //   < 1.5: default fg, > 1.5: cyan, > 2.0: magenta, > 2.5: gold.
    // Buff-boosted mult past 3 sticks to gold.
    let mc = "var(--fg, #fff)";
    if (mult >= 2.5) mc = "#ffd54a";
    else if (mult >= 2.0) mc = "var(--accent-2, #ff3df0)";
    else if (mult >= 1.5) mc = "var(--accent, #00e0ff)";
    hudMult.style.color = mc;
  },
  onRunEnd: (result: RunResult) => {
    setOverlayMessage("over", result.score);
    btnShare.hidden = true;
    hud.dataset.submitted = "false";
    // Platform v4: record XP + show toast.
    const xp = recordProfileRun({ game: "drift", score: result.score });
    showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
    if (isPlatformDailyRun()) markDailyComplete("drift", result.score);

    // Run summary card — passes / misses / best streak / biomes touched.
    const passes = result.stats.passes;
    const misses = result.stats.misses;
    const acc = passes + misses > 0 ? Math.round((passes / (passes + misses)) * 100) : 0;
    const biomesText = result.stats.biomeIdxs.map((i) => BIOMES[i]?.name ?? "?").join(" · ") || "—";
    // Speedrun PB tracking: only meaningful when target was reached.
    const speedrunActive = loadSettings().speedrun;
    let speedrunRow = "";
    if (speedrunActive && result.score >= 10000) {
      const dur = result.durationS;
      const prev = parseFloat(localStorage.getItem("aether.speedrun_pb") || "0");
      const isNew = prev === 0 || dur < prev;
      if (isNew) {
        try { localStorage.setItem("aether.speedrun_pb", dur.toFixed(2)); } catch {}
      }
      const label = isNew ? "Time to 10k (new best!)" : `Time to 10k (best ${prev.toFixed(1)}s)`;
      speedrunRow = `<div class="full"><dt>${label}</dt><dd>${dur.toFixed(2)}s</dd></div>`;
    }
    // Update lifetime peaks used by the achievement progress bars.
    try {
      const prevStreak = parseInt(localStorage.getItem("aether.lifetime_streak_best") || "0", 10) || 0;
      if (result.stats.bestStreak > prevStreak) {
        localStorage.setItem("aether.lifetime_streak_best", String(result.stats.bestStreak));
      }
    } catch { /* quota */ }

    // Append this run to local history and render a tiny sparkline.
    recordRun(result.score);
    const history = loadHistory();
    const sparkRow = history.length >= 2
      ? `<div class="full sparkline"><dt>Last ${history.length}</dt><dd>${renderSparkline(history)}</dd></div>`
      : "";
    const heatmapRow = result.stats.misses > 0
      ? `<div class="full sparkline"><dt>Miss lateral</dt><dd>${renderHeatmap()}</dd></div>`
      : "";
    runStats.innerHTML = `
      <div><dt>Passes</dt><dd>${passes}</dd></div>
      <div><dt>Misses</dt><dd>${misses}</dd></div>
      <div><dt>Accuracy</dt><dd>${acc}%</dd></div>
      <div><dt>Best streak</dt><dd>${result.stats.bestStreak}</dd></div>
      ${speedrunRow}
      <div class="full"><dt>Biomes</dt><dd>${biomesText}</dd></div>
      ${sparkRow}
      ${heatmapRow}`;
    runStats.hidden = false;

    // Stop replay capture and surface a download button if anything landed.
    replay.stop().then((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const btn = document.getElementById("btn-replay") as HTMLAnchorElement | null;
      if (!btn) return;
      btn.href = url;
      btn.download = `aether-drift-${Date.now()}.${replay.extension()}`;
      btn.hidden = false;
    });

    // Sharable seed link — only meaningful for non-ranked runs (daily or
    // URL-seeded). For ranked runs the server-issued seed is private and
    // can't be reconstructed from the client, so the button stays hidden.
    const seedBtn = document.getElementById("btn-seed-link") as HTMLButtonElement | null;
    const activeSeed = loadSettings().daily ? dailySeed() : urlSeed;
    if (seedBtn && activeSeed !== null) {
      seedBtn.hidden = false;
      seedBtn.onclick = async () => {
        const link = `${location.origin}/?seed=${activeSeed}`;
        try {
          await navigator.clipboard.writeText(link);
          seedBtn.textContent = "Seed link copied!";
          setTimeout(() => (seedBtn.textContent = "Copy seed link"), 1500);
        } catch {
          window.prompt("Copy this seed link:", link);
        }
      };
    }

    // Personal best — local-only, keyed on the persisted handle. We append a
    // "Personal best" badge to the score line when the new score beat the
    // previous record; otherwise show the previous best as context. No PB
    // tracking for runs without a known handle.
    // Daily runs use a separate PB pool keyed by date so today's best
    // resets at midnight without clobbering the all-time per-handle PB.
    const isDailyRun = loadSettings().daily;
    if (isDailyRun) {
      const isNewDaily = recordDailyBest(result.score);
      const bestToday = getDailyBest();
      // Bump the consecutive-days streak (idempotent per day) and surface it.
      const streak = bumpDailyStreak();
      const streakSuffix = streak >= 2 ? ` · ${streak}-day streak` : "";
      if (isNewDaily && result.score > 0) {
        msgBody.textContent = `Final ${result.score.toLocaleString()} · new daily best${streakSuffix}`;
      } else if (bestToday > 0) {
        msgBody.textContent = `Final ${result.score.toLocaleString()} · today's best ${bestToday.toLocaleString()}${streakSuffix}`;
      }
    } else {
      const handle = handleInput.value || savedHandle;
      if (handle) {
        const isNew = recordScore(handle, result.score);
        const best = getBest(handle);
        if (isNew && result.score > 0) {
          msgBody.textContent = `Final ${result.score.toLocaleString()} · new personal best`;
        } else if (best > 0) {
          msgBody.textContent = `Final ${result.score.toLocaleString()} · best ${best.toLocaleString()}`;
        }
      }
    }

    // Stash for the submit handler; user must explicitly confirm their handle.
    pendingRun = result;

    if (result.events.length > 0 && result.runId) {
      // Show the name-entry form, pre-filled with the last-used handle.
      submitForm.hidden = false;
      handleInputOverlay.value = handleInput.value || savedHandle || "";
      // Focus only on desktop — autofocus on mobile pops the keyboard
      // immediately and obscures the score.
      const isCoarse = matchMedia("(pointer: coarse)").matches;
      if (!isCoarse) {
        setTimeout(() => handleInputOverlay.focus({ preventScroll: true }), 80);
      }
      // Keyboard users get Tab cycling between the handle input and Save.
      overlayTrap.activate(submitForm);
    } else {
      // No events recorded (e.g. they hit time=0 instantly) — nothing to submit.
      submitForm.hidden = true;
    }
  },
});

setOverlayMessage("idle");

// Virtual joystick: only meaningful on touch devices; CSS hides on desktop.
new VirtualJoystick(document.body, (x, y) => {
  if (x === 0 && y === 0) {
    game.inputSystem.externalVec = null;
  } else {
    game.inputSystem.externalVec = { x, y };
  }
});

// Tilt-to-steer (mobile). Same external-vec channel as the joystick; whoever
// writes last wins for the frame. iOS gates permission behind a gesture, so
// we wire the enable() call to the settings toggle (which IS a gesture).
const gyro = new Gyro((v) => {
  game.inputSystem.externalVec = v;
});

// Settings drawer: persisted prefs feed back into the engine on every change.
new SettingsDrawer(document.body, {
  onChange: (s: Settings) => {
    // reduced-motion class must flip BEFORE applyQualityProfile so the engine
    // sees the right state when it asks "should motion effects be enabled?".
    document.body.classList.toggle("reduced-motion", s.reducedMotion);
    game.setReducedMotion(s.reducedMotion);
    game.applyQualityProfile(profileFor(s.quality));
    game.setAudioVolume(s.audioVolume);
    game.setMuted(s.muted);
    document.body.classList.toggle("cb-safe", s.colorblindSafe);
    game.setPractice(s.practice);
    document.body.classList.toggle("practice-mode", s.practice);
    // Endless mode is mutually exclusive with daily (a fixed seed implies
    // a fixed-length comparison). Daily wins if both are toggled.
    const endless = s.endless && !s.daily;
    game.setEndless(endless);
    document.body.classList.toggle("endless-mode", endless);
    // Sync the start-screen pill UI with the persisted value.
    const timedPill = document.querySelector<HTMLButtonElement>('[data-mode="timed"]');
    const endlessPill = document.querySelector<HTMLButtonElement>('[data-mode="endless"]');
    if (timedPill && endlessPill) {
      timedPill.classList.toggle("on", !endless);
      timedPill.setAttribute("aria-pressed", String(!endless));
      endlessPill.classList.toggle("on", endless);
      endlessPill.setAttribute("aria-pressed", String(endless));
    }
    // Leaderboard panel re-scopes to whichever board this mode submits to.
    // selectLbTab keeps the heading + tab visual state in lockstep.
    selectLbTab(endless ? "drift-endless" : "drift");
    // Daily challenge: deterministic seed for today, local-only run.
    // Seed priority: settings.daily > URL ?seed=NNNN > server-issued ranked.
    // Platform-wide daily seed (?daily=YYYY-MM-DD) takes priority — gives
    // Drift the same seed as the cross-game daily landing page.
    const platformDaily = getPlatformDailySeed();
    const overrideSeed = platformDaily ?? (s.daily ? dailySeed() : urlSeed);
    game.setDailySeed(overrideSeed);
    document.body.classList.toggle("daily-mode", s.daily);
    document.body.classList.toggle("seeded-mode", !s.daily && urlSeed !== null);
    // Surface the active seed as a small HUD badge so the player can see at
    // a glance whether this run is daily, url-seeded, or ranked.
    const seedBadge = document.getElementById("seed-badge");
    if (seedBadge) {
      if (s.daily) {
        seedBadge.textContent = `DAILY · ${todayKey()}`;
        seedBadge.hidden = false;
      } else if (urlSeed !== null) {
        seedBadge.textContent = `SEED · ${urlSeed}`;
        seedBadge.hidden = false;
      } else {
        seedBadge.hidden = true;
      }
    }
    // Ship palette — apply selected variant if it's unlocked, else fall back
    // to default. Unlock check uses the highest local PB the player has set.
    const bestEverHandle = handleInput.value || savedHandle;
    const bestEver = bestEverHandle ? getBest(bestEverHandle) : 0;
    const earned = new Set(ach.allEarned());
    const unlocked = unlockedVariants(bestEver, earned).map((v) => v.id);
    const variantId = unlocked.includes(s.shipVariant) ? s.shipVariant : "default";
    const v = getVariant(variantId);
    // Hue shift: rotates each color's hue by N degrees. Lightness +
    // saturation preserved so the palette's "feel" carries through.
    const rotated = s.hueShift > 0 ? {
      hullColor: v.hullColor,
      hullEmissive: rotateHueHex(v.hullEmissive, s.hueShift),
      wingEmissive: rotateHueHex(v.wingEmissive, s.hueShift),
      discColor: rotateHueHex(v.discColor, s.hueShift),
      haloColor: rotateHueHex(v.haloColor, s.hueShift),
    } : v;
    game.setShipPalette(rotated);
    // Streak-trail palette — locked palettes silently fall back to
    // "ribbon" inside game.ts (effectivePalette). The drawer renders
    // locked options as disabled so a user can't pick one anyway.
    game.setTrailPalette(s.trailVariant);
    // Gyro: enable on first toggle (user gesture, satisfies iOS permission
    // prompt). Disable removes the listener and clears any held vec.
    if (s.gyro) gyro.enable();
    else gyro.disable();
    // Ghost ship: feed game's render-time gate. Recording always runs so
    // toggling on next run gives you an immediate ghost; only playback
    // visibility is gated.
    game.setGhostEnabled(s.ghost);
    // Speedrun: race-to-10k. Setting target to 0 disables; non-zero ends
    // the run the frame it's crossed.
    game.setSpeedrunTarget(s.speedrun ? 10000 : 0);
    document.body.classList.toggle("speedrun-mode", s.speedrun);
    game.setModifiersEnabled(s.modifiers);
  },
});

// Turnstile guard is lazy-initialized when the runtime config says it's on.
let turnstile: TurnstileGuard | null = null;
fetchConfig().then((cfg) => {
  if (cfg.turnstile_enabled && cfg.turnstile_sitekey) {
    turnstile = new TurnstileGuard(cfg.turnstile_sitekey);
  }
});

// CTAs
async function startRun() {
  // iOS Safari rule: AudioContext can only be created inside a user-gesture
  // *synchronously*. Once we await anything, the gesture context is gone and
  // `new AudioContext()` is rejected. Prime audio FIRST, then do anything async.
  game.armAudio();

  document.querySelector("#play")?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Get a fresh Turnstile token. On iOS Safari the widget can stall for the
  // full 8s timeout; the bypass-on-timeout path (returns null) is what lets
  // the game still start when Turnstile flakes.
  const token = turnstile ? await turnstile.execute() : null;
  await game.start(token);
}

btnStart?.addEventListener("click", () => {
  if (game.currentState === "paused") game.resume();
  else startRun();
});

btnPlay?.addEventListener("click", () => {
  startRun();
});

btnPause.addEventListener("click", () => {
  if (game.currentState === "running") game.pause();
  else if (game.currentState === "paused") game.resume();
});

// Endless-mode: explicit end-run button on the HUD bottom row. Visible
// only while running/paused AND endless is active (managed in
// onStateChange below). One-tap end — confirmation would interrupt the
// "I'm done now" moment more than it'd protect against misclicks.
const btnEndRun = document.getElementById("btn-end-run") as HTMLButtonElement | null;
btnEndRun?.addEventListener("click", () => game.endRun());

// Start-screen mode pills. Clicking a pill rewrites settings.endless
// directly via storage, then routes through SettingsDrawer's onChange
// path so the leaderboard scope + game state stay coherent. Falls back
// to the drawer if the pills aren't present in DOM.
function setEndlessFromPill(want: boolean) {
  const s = loadSettings();
  // Daily takes priority — if daily is on, refuse the endless switch and
  // surface a hint via the body class so the player sees the badge.
  if (s.daily && want) return;
  if (s.endless === want) return;
  const next = { ...s, endless: want };
  saveSettings(next);
  // Re-mount the change to the engine + UI via the existing channel.
  // Simulating a manual onChange call by reloading the relevant slices.
  game.setEndless(want);
  document.body.classList.toggle("endless-mode", want);
  const timedPill = document.querySelector<HTMLButtonElement>('[data-mode="timed"]');
  const endlessPill = document.querySelector<HTMLButtonElement>('[data-mode="endless"]');
  if (timedPill && endlessPill) {
    timedPill.classList.toggle("on", !want);
    timedPill.setAttribute("aria-pressed", String(!want));
    endlessPill.classList.toggle("on", want);
    endlessPill.setAttribute("aria-pressed", String(want));
  }
  selectLbTab(want ? "drift-endless" : "drift");
}
document.querySelector('[data-mode="timed"]')?.addEventListener("click", () => setEndlessFromPill(false));
document.querySelector('[data-mode="endless"]')?.addEventListener("click", () => setEndlessFromPill(true));

// Leaderboard scope tabs — independent of the run-mode pill. A player
// might want to view the endless board while still running timed.
function selectLbTab(scope: "drift" | "drift-endless") {
  leaderboard.setScope(scope);
  const heading = document.getElementById("lb-heading");
  if (heading) heading.textContent = scope === "drift-endless" ? "Top endless drifters" : "Top drifters";
  document.querySelectorAll<HTMLButtonElement>("[data-lb-scope]").forEach((b) => {
    const on = b.dataset.lbScope === scope;
    b.classList.toggle("on", on);
    b.setAttribute("aria-selected", String(on));
  });
}
document.querySelector('[data-lb-scope="drift"]')?.addEventListener("click", () => selectLbTab("drift"));
document.querySelector('[data-lb-scope="drift-endless"]')?.addEventListener("click", () => selectLbTab("drift-endless"));

// Live-validate the handle input. Show the friendly error inline as the
// user types; the form-submit handler also re-checks before the network call.
const HANDLE_RE = /^[A-Za-z0-9_-]{1,16}$/;
function setHandleValidity(value: string) {
  const valid = HANDLE_RE.test(value);
  handleInputOverlay.classList.toggle("invalid", value.length > 0 && !valid);
  handleError.hidden = value.length === 0 || valid;
  return valid;
}
handleInputOverlay.addEventListener("input", () => setHandleValidity(handleInputOverlay.value));
handleInputOverlay.addEventListener("blur", () => setHandleValidity(handleInputOverlay.value));

// Post-drift submission: explicit confirm step. User types their name (or
// keeps the pre-filled value), presses Enter on mobile keyboard or taps
// Save score. We only call finishRun() once per pending run.
submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!pendingRun || !pendingRun.runId) return;

  const raw = (handleInputOverlay.value || "").trim().slice(0, 16);
  if (!setHandleValidity(raw)) {
    // Surface the error and keep focus on the input so the user can fix it.
    handleInputOverlay.focus();
    return;
  }
  const handle = raw;

  // Keep the in-HUD tag in sync + persist
  handleInput.value = handle;
  localStorage.setItem("aether.handle", handle);

  // Lock the form so a double-tap can't double-submit
  btnSubmitScore.disabled = true;
  btnSubmitScore.textContent = "Saving…";

  const run = pendingRun;
  pendingRun = null; // single-shot — even on failure we don't auto-retry here

  const res = await finishRun({
    run_id: run.runId!,
    handle,
    events: run.events,
  });
  leaderboard.setHighlight(res?.rank ?? null);

  if (res) {
    msgBody.textContent = `Final ${run.score.toLocaleString()} · ${rankBlurb(res)}`;
    // Crawler-friendly share URL — wraps the SVG card in an HTML page with
    // og: + twitter: meta tags so Twitter/LinkedIn/Discord/iMessage render
    // the preview tile instead of just linking the raw SVG.
    const sharedSeed = getPlatformDailySeed() ?? urlSeed;
    const seedQs = sharedSeed !== null ? `&game=drift&seed=${sharedSeed}` : "";
    const shareUrl = `${location.origin}/share?handle=${encodeURIComponent(handle)}&score=${run.score}&rank=${res.rank}${seedQs}`;

    // X (Twitter) intent — prefilled tweet. We use the /share page (not the
    // raw OG endpoint) so X expands the preview tile.
    const xBtn = document.getElementById("btn-share-x") as HTMLAnchorElement | null;
    if (xBtn) {
      const tweet = `Scored ${run.score.toLocaleString()} on Aether Drift, ranked #${res.rank}. Glide through the lattice:`;
      xBtn.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(shareUrl)}`;
      xBtn.hidden = false;
    }
    btnShare.hidden = false;
    btnShare.onclick = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        btnShare.textContent = "Copied!";
        setTimeout(() => (btnShare.textContent = "Copy share card link"), 1500);
      } catch {
        window.open(shareUrl, "_blank");
      }
    };
  } else {
    msgBody.textContent = `Final ${run.score.toLocaleString()} · submission queued`;
  }

  // Reveal the "Run again" CTA + refresh the leaderboard
  hud.dataset.submitted = "true";
  btnSubmitScore.disabled = false;
  btnSubmitScore.textContent = "Save score";
  overlayTrap.deactivate();
  leaderboard.refresh();
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Escape") {
    if (game.currentState === "running") game.pause();
    else if (game.currentState === "paused") game.resume();
  }
  if (e.code === "Enter" && (game.currentState === "idle" || game.currentState === "over")) {
    startRun();
  }
});

// Pause on tab blur (clean UX) + resume AudioContext on return (iOS Safari
// aggressively suspends the context when the tab is backgrounded or the
// screen locks; the user gets silent audio until we explicitly resume).
document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    if (game.currentState === "running") game.pause();
  } else {
    game.resumeAudio();
  }
});

// ---- Mobile ergonomics ----

// iOS Safari AudioContext unlock: fires on the very first user gesture
// anywhere on the page, not just the play button. Some users enter the
// page, scroll, scroll, scroll, then tap — without this, the AudioContext
// is still suspended when game.start() runs.
function unlockAudio() {
  game.setMuted(loadSettings().muted);
  // setMuted internally drives the audio drone; arming happens in game.start()
  // but exposing the canonical unlock path through a real one-shot listener
  // ensures it's already attempted before the first run.
  window.removeEventListener("pointerdown", unlockAudio);
  window.removeEventListener("touchstart", unlockAudio);
  window.removeEventListener("keydown", unlockAudio);
}
window.addEventListener("pointerdown", unlockAudio, { once: true, passive: true });
window.addEventListener("touchstart", unlockAudio, { once: true, passive: true });
window.addEventListener("keydown", unlockAudio, { once: true });

// Rotate-to-landscape overlay: only on coarse-pointer devices in portrait,
// during the play screen. Tablet portrait is playable, so only nudge when
// width is narrow enough that landscape really helps.
const rotateOverlay = document.getElementById("rotate-overlay");
function evaluateRotateOverlay() {
  if (!rotateOverlay) return;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const portrait = window.innerWidth < window.innerHeight;
  const narrow = window.innerWidth < 600;
  rotateOverlay.hidden = !(coarse && portrait && narrow);
}
window.addEventListener("resize", evaluateRotateOverlay, { passive: true });
window.addEventListener("orientationchange", evaluateRotateOverlay);
evaluateRotateOverlay();

// Soft Android Chrome install prompt — captured here, surfaced as a tiny
// button on the hero panel. iOS Safari doesn't fire this; users add to home
// screen manually via the share sheet.
let installEvent: Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> } | null = null;
const btnInstall = document.getElementById("btn-install") as HTMLButtonElement | null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installEvent = e as never;
  if (btnInstall) btnInstall.hidden = false;
});
btnInstall?.addEventListener("click", async () => {
  if (!installEvent) return;
  await installEvent.prompt();
  await installEvent.userChoice.catch(() => {});
  installEvent = null;
  if (btnInstall) btnInstall.hidden = true;
});
window.addEventListener("appinstalled", () => {
  installEvent = null;
  if (btnInstall) btnInstall.hidden = true;
});
